import React, { FC, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'src/theming';
import { Button, Container, Divider, Grid, LinearProgress, TextField, Typography } from '@material-ui/core';
import Card from 'src/components/Card';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import InformationSection from './components/InformationSection';
import InstallationSection from './components/InstallationSection';
import YesNoField from './components/InstallationSection/components/YesNoField';
import SignatureField from 'src/scenes/components/SignatureField';
import TicketSelect from './components/TicketSelect';
import DatePicker from 'src/components/DatePicker';
import TimePicker from 'src/components/TimePicker';
import useTicket from 'src/hooks/api/useTicket';
import useSite from 'src/hooks/api/useSite';
import Ticket from 'src/interfaces/api/Ticket';
import InterventionOrder, { PostInterventionOrderBody } from 'src/interfaces/api/InterventionOrder';
import moment from 'moment';
import EmployeeSelect from 'src/components/ApiField/EmployeeSelect';
import { DomainSelect } from 'src/components/ApiField';
import styles from './View.styles';
import { useNotifier } from 'src/components/NotificationCenter/AlertNotificationCenter';
import { MdChevronLeft as BackIcon } from '@react-icons/all-files/md/MdChevronLeft';
import AskBeforeLeaving from 'src/components/AskBeforeLeaving';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import Badge from 'src/components/Badge';
import PhotoPicker from '../../../components/PhotoPicker';
import api from 'src/config/api';

const useStyles = createUseStyles(styles);

interface Props {
  onSaveInterventionOrder: (interventionOrder: PostInterventionOrderBody) => Promise<InterventionOrder>;
  goBack: () => void;
  interventionOrder?: InterventionOrder;
  loading?: boolean;
}

const View: FC<Props> = ({ goBack, loading, interventionOrder, onSaveInterventionOrder }) => {
  const classes = useStyles();

  const [isSendConfirmationDialogOpened, setIsSendConfirmationDialogOpened] = useState(false);

  const { get: getTicket } = useTicket();
  const { get: getSite } = useSite();
  const [domainFilter, setDomainFilter] = useState<string>('');
  const [error, setError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [ticketLoading, setTicketLoading] = useState(false);

  const { addNotification } = useNotifier();

  const defaultValues: PostInterventionOrderBody = useMemo(
    () => ({
      hasContract: false,
      toBilled: false,
      date: moment().format(),
      startTime: moment().format(),
      endTime: moment().format(),
      ticketId: '',
      socialReason: '',
      address: '',
      postalCode: '',
      city: '',
      price: 0,
      object: '',
      observations: '',
      additionalInformations: '',
      contactName: '',
      intervenantId: '',
      signature: '',
      workFinished: '',
      hasPicture: '',
      upstreamRate: '',
      downstreamRate: '',
      callTest: '',
      callLocationTest: '',
      scannerTest: '',
      faxTest: '',
      printerTest: '',
      tpeTest: '',
      portabilityTest: '',
      alarmTest: '',
      frankingMachineTest: '',
      mailTest: '',
      recipientEmails: '',
      attachments: [],
      toSend: false,
    }),
    []
  );

  const formMethods = useForm<PostInterventionOrderBody>({ defaultValues });
  const watchTicketId = formMethods.watch('ticketId');

  useEffect(() => {
    if (interventionOrder) {
      formMethods.reset({
        ...interventionOrder,
        workFinished: interventionOrder.workFinished ? 'true' : 'false',
        hasPicture: interventionOrder.hasPicture ? 'true' : 'false',
        alarmTest: interventionOrder.alarmTest ? 'true' : 'false',
        callLocationTest: interventionOrder.callLocationTest ? 'true' : 'false',
        callTest: interventionOrder.callTest ? 'true' : 'false',
        faxTest: interventionOrder.faxTest ? 'true' : 'false',
        frankingMachineTest: interventionOrder.frankingMachineTest ? 'true' : 'false',
        mailTest: interventionOrder.mailTest ? 'true' : 'false',
        portabilityTest: interventionOrder.portabilityTest ? 'true' : 'false',
        printerTest: interventionOrder.printerTest ? 'true' : 'false',
        scannerTest: interventionOrder.scannerTest ? 'true' : 'false',
        tpeTest: interventionOrder.tpeTest ? 'true' : 'false',
      });
    }
  }, [interventionOrder]);

  const getTicketInformation = async (ticketId: Ticket['id']) => {
    setTicketLoading(true);
    setError(undefined);

    try {
      const ticket = await getTicket(ticketId);

      if (ticket.siteId) {
        if (!interventionOrder) {
          const site = await getSite(ticket.siteId);

          formMethods.setValue('ticketId', ticketId, { shouldValidate: true });
          formMethods.setValue('socialReason', site.socialReason, { shouldValidate: true });
          formMethods.setValue('address', site.address, { shouldValidate: true });
          formMethods.setValue('postalCode', site.postalCode, { shouldValidate: true });
          formMethods.setValue('city', site.city, { shouldValidate: true });
          formMethods.setValue('intervenantId', ticket.supportEmployeeId, { shouldValidate: true });
        }

        setDomainFilter(ticket.domainId);
        setTicketLoading(false);
      } else {
        throw new Error("Aucun site n'est associé à ce ticket");
      }
    } catch (e) {
      setError(e.message);
      setTicketLoading(false);
    }
  };

  const handleDomainFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    formMethods.setValue('ticketId', '', { shouldValidate: true });
    const newDomain = event.target.value as string;
    setDomainFilter(newDomain);
  };

  useEffect(() => {
    if (watchTicketId) {
      getTicketInformation(watchTicketId);
    }
  }, [watchTicketId]);

  const handlePreSave = formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: false }));

  const handleSubmit = () => {
    setIsSendConfirmationDialogOpened(false);
    formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: true }))();
  };

  const onSubmit = async (interventionOrder: PostInterventionOrderBody) => {
    try {
      await onSaveInterventionOrder(interventionOrder);

      addNotification({
        title: "Bon d'intervention enregistré avec succès !",
        alertProps: { color: 'success' },
      });

      goBack();
      setFormError(undefined);
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <AskBeforeLeaving
        message="Le formulaire n'a pas été enregistré, êtes-vous sûr de vouloir partir ?"
        condition={formMethods.formState.isDirty && !formMethods.formState.isSubmitting}
      />
      <Container className={classes.root} maxWidth='md'>
        <FormProvider {...formMethods}>
          <Button className={classes.button} onClick={goBack}>
            <BackIcon size={25} /> Retour à la liste
          </Button>

          <Card>
            <Typography className={classes.title} variant='h5'>
              Bon d'intervention
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <DomainSelect fullWidth onChange={handleDomainFilterChange} value={domainFilter} />
              </Grid>

              <Grid item xs={12} sm={8}>
                <Controller
                  name='ticketId'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TicketSelect
                      label='Ticket'
                      domainId={domainFilter}
                      helperText={error?.message}
                      error={undefined !== error}
                      {...field}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name='date'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      {...field}
                      label='Date'
                      format='DD/MM/YYYY'
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  name='startTime'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TimePicker
                      {...field}
                      ampm={false}
                      label="Heure d'arrivée"
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  name='endTime'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TimePicker
                      {...field}
                      ampm={false}
                      label='Heure de départ'
                      inputVariant='outlined'
                      helperText={error?.message}
                      error={undefined !== error}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='overline' className={classes.section}>
                  Informations
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <InformationSection ticketLoading={ticketLoading} />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='object'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Objet de l'intervention"
                      variant='outlined'
                      multiline
                      minRows={2}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='observations'
                  control={formMethods.control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Observations'
                      variant='outlined'
                      multiline
                      minRows={4}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              {domainFilter !== 'a5dc2e57-367e-4b29-a8ea-8fa266dbe523' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant='overline' className={classes.section}>
                      Installation
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <InstallationSection />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider component='div' />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='additionalInformations'
                  control={formMethods.control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Compléments d'informations"
                      variant='outlined'
                      multiline
                      minRows={2}
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='workFinished'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <YesNoField
                      {...field}
                      error={undefined !== error}
                      helperText={error?.message}
                      label='Travail terminé ?'
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='hasPicture'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <YesNoField
                      {...field}
                      error={undefined !== error}
                      helperText={error?.message}
                      label='Photo avant / après'
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='attachments'
                  control={formMethods.control}
                  render={({ field: { ref, ...otherField }, fieldState: { error } }) => (
                    <PhotoPicker
                      {...otherField}
                      error={undefined !== error}
                      helperText={error?.message}
                      itemId={interventionOrder?.id}
                      getItemAttachmentApi={(attachmentId: string) =>
                        api.interventionOrder.getAttachment(interventionOrder!.id, attachmentId)
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name='contactName'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Nom du contact'
                      variant='outlined'
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name='intervenantId'
                  control={formMethods.control}
                  rules={{ required: { value: true, message: 'Le champ est requis' } }}
                  render={({ field, fieldState: { error } }) => (
                    <EmployeeSelect
                      {...field}
                      multiple={false}
                      fullWidth
                      label="Nom de l'intervenant"
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                {interventionOrder?.signatureId ? (
                  <Badge color='primary' text='Signature déjà enregistré !' />
                ) : (
                  <SignatureField
                    mainText='Le client reconnaît que son installation est en bon état de fonctionnement après intervention du technicien.'
                    footerText={
                      <>
                        Chaque intervention facturable comporte au minimum un forfait de déplacement et une heure
                        d’intervention au tarif en vigueur.
                        <br />
                        Chaque heure entamée est due. Le client reconnaît avoir pris connaisance des tarifs des pièces,
                        celle-ci étant obligatoirement facturée en sus dans le cadre de la maintenance ou de dépannage
                        n’incluant pas ces pièces. Clause de réserve de propriété (loi 12.05.80) : la marchandise et les
                        fournitures resteront notre propriété jusqu’au jour de leur règlement intégral.
                        <br />
                        Seul le tribunal du siège du siège social de la société intervenante est compétent en cas de
                        recours.
                      </>
                    }
                  />
                )}
              </Grid>

              <Grid className={classes.column} item xs={12}>
                <Typography variant='overline' className={classes.section}>
                  Envoyez le bon d'intervention aux adresses suivantes
                </Typography>

                <Typography variant='caption' className={classes.section}>
                  Séparez les mails par des virgules
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='recipientEmails'
                  control={formMethods.control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label='Adresses mails'
                      variant='outlined'
                      fullWidth
                      error={undefined !== error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              {formError && (
                <Grid item xs={12}>
                  <p className={classes.error}>{formError}</p>
                </Grid>
              )}

              {!interventionOrder?.sended && (
                <>
                  <Grid item xs={8}>
                    <Button
                      fullWidth
                      color='primary'
                      variant='outlined'
                      disabled={formMethods.formState.isSubmitting}
                      onClick={handlePreSave}
                    >
                      Pre-enregistrer
                    </Button>
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      color='primary'
                      variant='contained'
                      disabled={formMethods.formState.isSubmitting}
                      onClick={() => setIsSendConfirmationDialogOpened(true)}
                    >
                      Envoyer
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>

            {(loading || formMethods.formState.isSubmitting) && <LinearProgress style={{ marginTop: 5 }} />}
          </Card>
        </FormProvider>
      </Container>

      <ConfirmationDialog
        open={isSendConfirmationDialogOpened}
        title='Attention'
        description={`Vous êtes sur le point d'envoyer le bon d'intervention.\nIl sera ajouté sur le mur du client puis envoyé aux adresses que vous avez défini.\nUne fois envoyé, il vous sera impossible de l'editer.`}
        confirmationText='Valider'
        onValidate={handleSubmit}
        onClose={() => setIsSendConfirmationDialogOpened(false)}
      />
    </MuiPickersUtilsProvider>
  );
};

export default View;
